import { Box } from '@mui/material';

import { IntakePageFooter } from './IntakePageFooter';
import { IntakePageHeader } from './IntakePageHeader';
import { DOCUMENT_HINTS, RAIL_SECTIONS } from './navigation';
import { cardSx, groupTitleSx, wf } from './tokens';

export interface ForecastPage {
  pageKey: string;
  label: string;
  fieldCount: number;
  requiredCount: number;
  active: boolean;
}

export interface ForecastDocument {
  fieldId: string;
  label: string;
  detail?: string;
}

interface IntakeForecastPageProps {
  /** Counts computed from the generated form, keyed by page. */
  pages: Record<string, { fieldCount: number; requiredCount: number; active: boolean }>;
  documents: ForecastDocument[];
  totalRequired: number;
  onContinue: () => void;
  onBack: () => void;
  nextLabel: string;
}

function SectionCard({
  title,
  items,
}: {
  title: string;
  items: (ForecastPage & { hint?: string })[];
}) {
  return (
    <Box sx={cardSx}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
        <Box sx={groupTitleSx}>{title}</Box>
      </Box>
      {items.map(item => (
        <Box
          key={item.pageKey}
          sx={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            fontSize: '12.5px',
            padding: '3px 0',
            color: wf.ink,
          }}
        >
          <Box
            component="span"
            sx={{ width: '14px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <Box
              component="span"
              sx={{
                width: '9px',
                height: '9px',
                borderRadius: '50%',
                border: `1.4px solid ${wf.faint}`,
                display: 'block',
              }}
            />
          </Box>
          <Box component="span">{item.label}</Box>
          <Box
            component="span"
            sx={{ marginLeft: 'auto', color: wf.muted, fontSize: '11px', textAlign: 'right' }}
          >
            {item.hint ?? `${item.requiredCount} required`}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export function IntakeForecastPage({
  pages,
  documents,
  totalRequired,
  onContinue,
  onBack,
  nextLabel,
}: IntakeForecastPageProps) {
  const build = (sectionKey: string) => {
    const section = RAIL_SECTIONS.find(entry => entry.key === sectionKey);
    return (section?.items ?? [])
      .filter(item => !['K1', 'F1'].includes(item.pageKey))
      .map(item => ({
        pageKey: item.pageKey,
        label: item.label,
        hint: DOCUMENT_HINTS[item.pageKey],
        fieldCount: pages[item.pageKey]?.fieldCount ?? 0,
        requiredCount: pages[item.pageKey]?.requiredCount ?? 0,
        active: pages[item.pageKey]?.active ?? false,
      }))
      .filter(item => item.active);
  };
  const clinicSetupPages = build('clinic_setup');
  const providerIntakePages = build('your_intake');

  return (
    <>
      <IntakePageHeader
        title="Here's everything we'll need"
        sub={`The whole list, up front. Gather the documents first — they fill most fields for you. ${totalRequired} required items for your setup.`}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: '10px',
        }}
      >
        {clinicSetupPages.length > 0 && (
          <SectionCard title="Clinic setup" items={clinicSetupPages} />
        )}
        {providerIntakePages.length > 0 && (
          <SectionCard title="Your intake" items={providerIntakePages} />
        )}
      </Box>

      {documents.length > 0 && (
        <>
          <Box sx={cardSx} component="section" aria-labelledby="intake-documents-to-gather">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <Box id="intake-documents-to-gather" sx={groupTitleSx}>
                Documents to gather before you start
              </Box>
            </Box>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                gap: '10px',
              }}
            >
              {documents.map(document => (
                <Box
                  key={document.fieldId}
                  sx={{
                    border: `1px solid ${wf.line}`,
                    borderRadius: '6px',
                    padding: '7px 10px',
                    minHeight: '34px',
                    fontSize: '12.5px',
                  }}
                >
                  <Box sx={{ color: wf.muted, fontSize: '10.5px' }}>{document.label}</Box>
                  {document.detail && <Box>{document.detail}</Box>}
                </Box>
              ))}
            </Box>
          </Box>
          <Box
            sx={{
              fontSize: '11.5px',
              color: wf.muted,
              background: wf.surfaceAlt,
              borderRadius: '6px',
              padding: '8px 10px',
            }}
          >
            A few optional items (references, portal logins, payment setup) come after you finish —
            we won&apos;t show them until then.
          </Box>
        </>
      )}

      <IntakePageFooter primaryLabel={nextLabel} onPrimary={onContinue} onBack={onBack} />
    </>
  );
}
